/**
 * Theme Resolver
 *
 * Maps BrandTokens → partial overrides for every affected screen/component theme.
 * The result is shallow-merged on top of the base screenTheme, so only
 * brand-colored properties change — structural layout is untouched.
 *
 * Cache key = `<themeId>:<mode>` so overrides are computed once per theme.
 */
import type { BrandTokens } from './types';
import { palette } from '../colorGroups/palette.colors';

/** Partial override bag keyed by screen name from screenThemes */
export type ClientThemeOverrides = Record<string, Record<string, string>>;

const _cache = new Map<string, ClientThemeOverrides>();

/**
 * Resolve brand tokens into per-screen property overrides.
 *
 * Brand property → screen theme key mapping (from Figma spec):
 *
 *  primary       → button BG, active-tab bg, shift banner, icon-toggle active,
 *                  selected pagination, leaderboard self-card, WO-number links
 *  primaryHover  → (internal hover — used by CSS/web hover rules)
 *  primaryBadge  → active tab badge fill (the count badge inside the active tab)
 *  primaryTint   → inactive-tab badge fill, selected sidebar tab bg, selected row bg
 *  primaryBorder → outlined/secondary button border, sidebar selected border,
 *                  pagination button outline
 *  onPrimary     → text/icons on solid primary fills
 *  onTint        → text on tint/outlined areas
 */
export function resolveClientOverrides(
  tokens: BrandTokens,
  cacheKey: string,
): ClientThemeOverrides {
  if (_cache.has(cacheKey)) {
    return _cache.get(cacheKey)!;
  }

  const overrides: ClientThemeOverrides = {
    // ─── Agency Selection / Login screens ────────────────────────────────────
    loginScreen: {
      /** Page background — lightest brand tint canvas (matches main layout BG) */
      mainBackgroundColor: tokens.pageBackground,
      /** Card border — medium tint (#CCE3FF) matches outlined-button border */
      borderColor: tokens.primaryTintMed,
    },

    // ─── Button ───────────────────────────────────────────────────────────────
    button: {
      primaryBG: tokens.primary,
      primartText: tokens.onPrimary,
      primaryHoverBG: tokens.primaryHover,
      primaryPressedBG: tokens.primaryTintMed,
      bGEnableColor: tokens.primary,
      textEnableColor: tokens.onPrimary,
      borderColor: tokens.primaryBorder,
      /** Secondary outline border = brand primaryBorder (Figma: outlined/secondary button border) */
      secondaryBorderColor: tokens.primaryBorder,
      /** Secondary button bg = white fill (onPrimary = #FFFFFF).
       *  Declared here as a runtime override so it is never stale from the
       *  module-level screenThemes initialisation. */
      secondaryBG: tokens.onPrimary,
      /** Secondary button text = onTint (Figma: #020617 — dark text on transparent/tint bg) */
      secondaryText: tokens.onTint,
      /** Secondary button hover fill = primaryTint (Figma: #E6F1FF hover fill) */
      secondaryHoverBG: tokens.primaryTint,
      /** Secondary button pressed fill = primaryTintMed (Figma: #CCE3FF pressed fill) */
      secondaryPressedBG: tokens.primaryTintMed,
      // /** Disabled button background — lightest brand tint so disabled state is
      //  *  on-brand but clearly muted (e.g. #CCE3FF for blue, #FFCDD2 for red).
      //  *  Overrides the static gray in screenThemes so every brand gets its own
      //  *  disabled colour rather than sharing a generic grey-blue. */
      // bGDisableColor: tokens.primaryTintMed,
      // /** Disabled button text — white on the brand-tinted disabled BG,
      //  *  matching the enabled button's onPrimary text colour (#FFFFFF). */
      // textDisableColor: tokens.onPrimary,
      subTitleBadgeBg: tokens.primaryBadge
    },

    // ─── Header ───────────────────────────────────────────────────────────────
    header: {
      // Shift status badge background ("Status: Job Assignment")
      shiftStatus: tokens.primaryTint,
      statusText: tokens.primary,
      // CTAs in header that use primary fill
      CTAEnabledBG: tokens.primary,
      CTAEnabledBGText: tokens.onPrimary,
      IndirectActivityCTABorder: tokens.primaryBorder,
      IndirectActivityCTAText: tokens.primary,
      /** Indirect Activity icon color when button is disabled (#FFFFFF) */
      headerIndirectActivityDisabledIconColor: tokens.onPrimary,
    },

    // ─── Profile component ────────────────────────────────────────────────────
    profile: {
      // Avatar circle background and text use primary brand colour
      BGColor: tokens.primary,
      textColor: tokens.onPrimary,
    },

    // ─── Nova AI chat bubble ──────────────────────────────────────────────────
    novaAIScreen: {
      // Floating Nova AI toggle button background
      novaAIIconBGColor: tokens.primary,
      botIconBGColor: tokens.primary,
    },

    // ─── Dashboard screen ─────────────────────────────────────────────────────
    DashboardScreen: {
      // Page background — app-level lightest canvas (matches main layout BG)
      background: tokens.pageBackground,

      // Shift banner header background
      shiftBGColor: tokens.primary,

      // Work-order card default border — uses brand primaryTint (#E6F1FF per Figma Default state)
      tileViewBorder: tokens.primaryTint,
      shiftSummaryBorder:  tokens.primaryTintMed,
      partsViewBorder: tokens.primaryTint,

      // Work-order card hover/selected border — uses brand primary (#005FCC → brand primary)
      tileViewSelectedBorder: tokens.primary,

      // Work-order card background — white fill (Figma Default: #FFFFFF)
      listViewBG: tokens.onPrimary,

      // Main Repairs / Parts Inventory tab — active fill + inactive underline
      workOrderFilterSelectedTextBorder: tokens.primary,
      // Outer rounded container — white background, medium-tint border
      tabContainerBG: tokens.onPrimary,
      tabContainerBorder: tokens.primaryTintMed,

      // Filter tabs — active state ("All" pill)
      selectedCountBG: tokens.primary,
      selectedCountTextColor: tokens.onPrimary,
      tabIconSelectedColor: tokens.onPrimary,
      tabTextSelectedColor: tokens.onPrimary,

      // Filter tabs — inactive state ("Open 2" badge)
      countBG: tokens.primaryTintMed,
      countTextColor: tokens.primary,

      // Prev / Next pagination button text
      listPaginationButtonText: tokens.primaryBorder,

      // WO Number / Repair ID link text
      repairIdValueColor: tokens.primary,
      workOrderIdValueColor: tokens.primary,
      repairNumber: tokens.primary,

      // Tile/List view toggle icons — active button: primary blue BG, white icon
      workOrderTileViewIcon: tokens.primary,
      workOrderTileViewSelectedIcon: tokens.onPrimary,

      // Card notification icon colors
      cardNotificationIconColor: tokens.primary,
      cardNotificationBGColor: tokens.primaryTint,

      // Notification badge
      notificationCountBGColor: tokens.primary,
      notificationCountTextColor: tokens.onPrimary,

      // List view table: header row BG, data row BG, borders and separators
      listViewHeaderBG: tokens.primaryTint,
      listViewRowBG: tokens.onPrimary,
      listViewBorder: tokens.primaryTintMed,
      separatorColor: tokens.primaryTintMed,
      listViewHeaderText: tokens.onTint,

      // Leaderboard self-card highlight
      leaderBoardCardSelfLeaderBG: tokens.primary,
      leaderBoardCardSelfLeaderName: tokens.onPrimary,
      repairBoardCardBorder: tokens.primaryTint,
      subTitleBadgeBg: tokens.primaryBadge,
      sortIconColor: tokens.onTint,
      selectedSortIconColor: tokens.primary,
    },

    // ─── Sidebar / Main layout ────────────────────────────────────────────────
    MainLayoutScreen: {
      // Page-level background — lightest tint canvas, per Figma app BG spec
      background: tokens.pageBackground,
      // Sidebar / menu background — white fill
      sidebarBG: tokens.onPrimary,
      // Selected nav item — active tab fill (primaryTint) + outline (primary per Figma)
      selectedTab: tokens.primaryTint,
      selectedTabBorder: tokens.primary,
      // Active icon / label colour
      activeLabel: tokens.primary,
      // Hover: same tint as active fill, no border (Figma hover state)
      tabHoverBg: tokens.primaryTint,
      // Pressed: medium tint, darker than hover (Figma pressed/focused state)
      tabPressedBg: tokens.primaryTintMed,
      /** Disabled menu-item background — grey card (#CED8E3) matching the second
       *  screenshot design where disabled items have a visible muted chip. */
      tabDisabledBg: palette.gray.tabDisabled,
      /** Disabled menu-item label text — dark navy (#3A4B61) for clear contrast
       *  on the grey chip background. */
      tabDisabledText: palette.gray.disabledCTAText,
      /** Disabled menu-item icon colour — dark navy (#3A4B61), consistent with
       *  the label colour across all brand themes. */
      tabDisabledIconColor: palette.gray.disabledCTAText,
    },

    // ─── Pagination ───────────────────────────────────────────────────────────
    pagination: {
      /** Active page button fill (#005FCC) */
      selectedPagination: tokens.primary,
      /** Active page number text (#FFFFFF) */
      selectedPaginationText: tokens.onPrimary,
      /** Prev/Next button text + inactive page number text (#020617) */
      listPaginationButtonText: tokens.primaryBorder,
      /** Page button border + Prev/Next button border (#CCE3FF) */
      listPaginationBorder: tokens.primaryTintMed,
      /** Prev/Next + inactive page button background (#FFFFFF) */
      paginationButtonBG: tokens.onPrimary,
      /** Prev/Next + inactive page number text (#020617) */
      paginationButtonText: tokens.onTint,
    },

    // ─── Shop home page ───────────────────────────────────────────────────────
    shopHomePage: {
      // Page background — lightest brand tint canvas (matches main layout BG)
      mainBackgroundColor: tokens.pageBackground,
      // Content container / filter border — medium tint (#CCE3FF)
      borderColor: tokens.primaryTintMed,
      listPaginationButtonText: tokens.primaryBorder,
      // Tile/List view toggle icons — active button: primary blue BG, white icon
      workOrderTileViewIcon: tokens.primary,
      workOrderTileViewSelectedIcon: tokens.onPrimary,
      // List view table: header row BG, data row BG, borders and separators
      // — mirrors DashboardScreen tokens so both list views look identical
      listViewHeaderBG: tokens.primaryTint,
      listViewRowBG: tokens.onPrimary,
      listViewBorder: tokens.primaryTintMed,
      separatorColor: tokens.primaryTintMed,
      listViewHeaderText: tokens.onTint,
      shopHomePageMessageIcon: tokens.primaryTint,
    },

    pinAuthentication: {
      pinAuthIconBGColor: tokens.primaryTint,
      pinAuthBorderColor: tokens.primary
    },

    cards: {
      scoreBoardCardBorder: tokens.primaryTint,
    },
    

    // ─── Repair Details ───────────────────────────────────────────────────────
    RepairDetailsScreen: {
      // Page background — matches theme tint
      background: tokens.pageBackground,
      tabsSelectedColor: tokens.primary,
      mainTabsSelectedColor: tokens.primary,
      selectedtabBGColor: tokens.primaryTint,
      selectedtabBorder: tokens.primarySoft,
      selectedCountBG: tokens.primary,
      saveCTABGColor: tokens.primary,
      saveCTATitleColor: tokens.onPrimary,
      saveCTAIconColor: tokens.onPrimary,
      // Tile/List view toggle icons — active button: primary blue BG, white icon
      workOrderTileViewIcon: tokens.primary,
      workOrderTileViewSelectedIcon: tokens.onPrimary,
      // Main tab pill container — white bg, medium-tint border
      tabContainerBG: tokens.onPrimary,
      tabContainerBorder: tokens.primaryTintMed,
      // Inactive tab text — brand primary
      tabsTextColor: tokens.primary,
      // Active tab text — white (on solid primary fill)
      tabsSelectedTextColor: tokens.onPrimary,
      maintabSelectedTextColor: tokens.onPrimary,
      // RepairShift section backgrounds
      repairShiftSectionBGColor: tokens.primaryTint,
      repairShiftMainSectionBGColor: tokens.pageBackground,
      notesBorder: tokens.primaryTintMed,
      notesFilterUnselectBGColor: tokens.primaryTint,
      viewNoteInfoBorder: tokens.primaryTintMed,
      noteSectionSelectedBG: tokens.primaryTint,
      noteSectionBorder: tokens.primaryTintMed,
      listeningEnableIconBG: tokens.primaryTint,
      listeningEnabledCTABG: tokens.primaryTint,
      noteSectionApplySelectedBG: tokens.primary,
      listViewHeaderBG: tokens.primaryTint,
      listViewRowBG: tokens.onPrimary,
      listViewBorder: tokens.primaryTintMed,
      separatorColor: tokens.primaryTintMed,
      listViewHeaderText: tokens.onTint,
      listViewSelectedRowBG: tokens.pageBackground,
      addPartsNoteBg: tokens.primaryTint,
      addPartsNoteBorder: tokens.primaryTintMed,
      ownPartsBorder: tokens.primaryTint,
      noteSectionSelectedBorder: tokens.primaryTint,
      noteHeaderSeparator: tokens.primaryTint,
      subTitleBadgeBg: tokens.primaryBadge,
      inputBorder: tokens.primaryTintMed,
      tabTextSelectedColor: tokens.onPrimary,
      workOrderFilterSelectedTextBorder: tokens.primary,
      /** Grouped dropdown panel open border — matches standard Dropdown openBorderColor */
      openBorderColor: tokens.primaryBorder,
      /** Grouped dropdown selected item background — matches standard Dropdown selectedDropdownBGColor */
      selectedDropdownBGColor: tokens.primaryTint,
      /** Grouped dropdown selected/expanded text color — matches standard Dropdown selectedDropdownTextColor */
      selectedDropdownTextColor: tokens.primary,
      /** Separator border between groups inside the grouped dropdown panel */
      groupedDropdownSeparatorColor: tokens.primaryTint,
      sortIconColor: tokens.onTint,
      selectedSortIconColor: tokens.primary,
      disabledText: palette.gray.tabDisabledText
    },

    // ─── Create Work Order flow ───────────────────────────────────────────────
    createWorkOrderFlow: {
      // Page background — matches theme tint
      pageBackground: tokens.primaryTint,
      primaryBG: tokens.primary,
      verifyBtnEnabledBG: tokens.primary,
      verifyBtnEnabledText: tokens.onPrimary,
      nextBtnEnabledBG: tokens.primary,
      nextBtnEnabledText: tokens.onPrimary,
      selectedCTA: tokens.primary,
      selectCTAText: tokens.onPrimary,
      // Tile/List view toggle icons — active button: primary blue BG, white icon
      workOrderTileViewIcon: tokens.primary,
      workOrderTileViewSelectedIcon: tokens.onPrimary,
      footerBorder: tokens.primaryTintMed,
      formInputBorder: tokens.primaryTint,
      listViewBorder: tokens.primaryTintMed,
      assetDetailsDivider: tokens.primaryTintMed,
      listViewHeaderBG: tokens.primaryTint,
      // Filter tabs — inactive state ("Open 2" badge)
      countBG: tokens.primaryTintMed,
      countTextColor: tokens.primary,
      workOrderStatusSelectedTextBG: tokens.primary,
      tabTextColor: tokens.primary,
      warrantyboxBorder: tokens.primaryBorder,
      workOrderIdValueColor: tokens.primary,
    },

    stepper: {
      activeCircleBorder: tokens.primary,
      completedCircleBG: tokens.primary,
      activeCircleText: tokens.primary,
      connectorActive: tokens.primary
    },

    // ─── Search Input ─────────────────────────────────────────────────────────
    searchInput: {
      /** Default border = primaryTintMed (#CCE3FF per Figma Regular state) */
      borderColor: tokens.primaryTintMed,
      /** Hover border = primaryBorder (#66ABFF per Figma Hover state) */
      hoverBorderColor: tokens.primaryBorder,
      /** Focus / Active border = primary (#005FCC per Figma Active state) */
      activeBorderColor: tokens.primary,
      iconColor: tokens.primaryTintMed,
      searchResultBGColor: tokens.primaryTint,
    },

    // ─── Dropdown ─────────────────────────────────────────────────────────────
    dropdown: {
      /** Default (closed) trigger border — brand primaryBorder so every client
       *  theme renders its own brand colour instead of the blue base default.
       *  Also used for the item-separator line inside the open panel. */
      workOrderSortIconsBorder: tokens.primaryTintMed,
      /** Trigger button hover background = primaryTint (#E6F1FF per Figma hover state) */
      hoverBGColor: tokens.primaryTint,
      /** Item hover background = primaryTint (#E6F1FF per Figma hover state) */
      menuItemHoverBGColor: tokens.primaryTint,
      /** Open/active border — brand primaryBorder (#66ABFF blue) applied to
       *  trigger and panel when the dropdown is clicked / open / selected. */
      openBorderColor: tokens.primaryBorder,
      selectedDropdownBGColor: tokens.primaryTint,
      selectedDropdownTextColor: tokens.primary,
      searchResultBGColor: tokens.primaryTint,
      searchBorder: tokens.primaryBorder,
    },

    // ─── Checkbox ─────────────────────────────────────────────────────────────
    checkbox: {
      // ── Normal (unchecked) ──────────────────────────────────────────────
      /** Normal border = primaryBorder (#66ABFF for blue — Figma Normal: Button Outline) */
      listUnSelectedBorder: tokens.primaryBorder,
      // listUnSelectedBG is always white (#FFFFFF) — not brand-specific

      // ── Checked ─────────────────────────────────────────────────────────
      /** Checked fill = brand primary (#005FCC for blue — Figma Checked: Button Fill) */
      listSelectedBG: tokens.primary,
      // listSelectedBorder stays transparent — not overridden here

      // ── Hover (unchecked) ───────────────────────────────────────────────
      /** Hover fill = brand primaryTint (#E6F1FF for blue — Figma Hover: Button Fill) */
      hoverBG: tokens.primaryTint,
      /** Hover border = brand primaryBorder (#66ABFF for blue — Figma Hover: Button Outline) */
      hoverBorder: tokens.primaryBorder,

      // ── Pressed (unchecked) ─────────────────────────────────────────────
      /** Pressed fill = brand primaryTintMed (#CCE3FF for blue — Figma Pressed: Button Fill) */
      pressedBG: tokens.primaryTintMed,
      /** Pressed border = brand primaryBorder (#66ABFF for blue — Figma Pressed: Button Outline) */
      pressedBorder: tokens.primaryBorder,

      // ── borderColor (generic override) ──────────────────────────────────
      borderColor: tokens.primaryBorder,

      // Disabled states (#CED8E3 / #F1F5F9) are brand-neutral — not overridden here.
    },

    // ─── Text Input ───────────────────────────────────────────────────────────
    inputText: {
      /** Default border = primaryTintMed (#CCE3FF per Figma Regular state) */
      inputBorder: tokens.primaryTintMed,
      /** Hover border = primaryBorder (#66ABFF per Figma Hover state) */
      hoverBorderColor: tokens.primaryBorder,
      /** Focus / Active border = primary (#005FCC per Figma Active state) */
      activeBorderColor: tokens.primary,
    },
  };

  _cache.set(cacheKey, overrides);
  return overrides;
}

/**
 * Shallow-merge brand overrides onto a base screen-theme object.
 * Returns the base object unchanged when there are no overrides for the screen.
 */
export function applyClientOverride<T extends Record<string, string>>(
  base: T,
  overrides: Record<string, string> | undefined,
): T {
  if (!overrides) return base;
  return { ...base, ...overrides } as T;
}